/**
 * A task in the record panel: its state and timings, the failure in full,
 * what ran it, and Open logs. The logs stay on the task page (D7); this is
 * the fast read before deciding whether to go there.
 */
import { useQuery } from '@tanstack/react-query';

import { getTask } from '@optum/api-client/endpoints/tasks';
import type { Task } from '@optum/api-client/types';

import { type RecordRef, useRecordPanel } from '../../components/shell/record.js';
import {
  RecordActions,
  RecordAsync,
  RecordColumn,
  RecordFacts,
  RecordIdentity,
  RecordMessage,
  RecordOpen,
  RecordRelated,
  RecordSection,
} from '../../components/shell/RecordParts.js';
import { absoluteDateTime, shortId, thb } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { IconTile, Typography } from '../../ui/finixt.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { useTaskCost } from '../cost/CostPanel.js';

const TERMINAL = new Set<Task['status']>(['completed', 'failed', 'cancelled']);

function durationOf(t: Task): string {
  if (!t.started_at) return '—';
  const end = t.completed_at ? Date.parse(t.completed_at) : Date.now();
  const ms = Math.max(0, end - Date.parse(t.started_at));
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.round(ms / 1000)} s`;
}

function toneOf(status: Task['status']): { tint: string; color: string } {
  if (status === 'failed') return { tint: 'var(--danger-tint)', color: 'var(--danger)' };
  if (status === 'completed') return { tint: 'var(--ok-tint)', color: 'var(--ok)' };
  return { tint: 'var(--info-tint)', color: 'var(--info)' };
}

export function TaskRecord({ record }: { record: RecordRef }) {
  const panel = useRecordPanel();
  const id = record.id;
  const task = useQuery({ queryKey: qk.task(id), queryFn: () => getTask(id) });
  const terminal = task.data ? TERMINAL.has(task.data.status) : false;
  const cost = useTaskCost(id, terminal);

  return (
    <RecordAsync query={task} record={record}>
      {(t) => (
        <RecordColumn>
          <RecordIdentity
            avatar={<IconTile icon="task_alt" size={40} {...toneOf(t.status)} />}
            eyebrow={
              <>
                <Typography variant="eyebrow">Task</Typography>
                <StatusBadge status={t.status} vocabulary="task" />
              </>
            }
            name={<span className="mono">{t.id}</span>}
            subtitle={`${t.capability_id} · ${t.source}`}
          />
          <RecordSection label="Timing">
            <RecordFacts
              items={[
                { label: 'Created', value: absoluteDateTime(t.created_at) },
                { label: 'Started', value: t.started_at ? absoluteDateTime(t.started_at) : '—' },
                {
                  label: 'Finished',
                  value: t.completed_at ? absoluteDateTime(t.completed_at) : '—',
                },
                { label: 'Duration', value: durationOf(t) },
                {
                  label: 'Cost',
                  value: cost.data ? thb(cost.data.priced_total) : terminal ? '—' : 'running',
                },
              ]}
            />
          </RecordSection>
          {t.failure_reason ? (
            <RecordSection label="Failure">
              <RecordMessage tone="danger">{t.failure_reason}</RecordMessage>
            </RecordSection>
          ) : null}
          <RecordSection label="Ran as">
            <RecordRelated
              rows={[
                {
                  icon: 'smart_toy',
                  label: shortId(t.agent_id),
                  meta: 'agent',
                  onClick: () =>
                    panel.push({ kind: 'agent', id: t.agent_id, orgId: t.organization_id }),
                },
                {
                  icon: 'apartment',
                  label: shortId(t.organization_id),
                  meta: 'organization',
                  onClick: () => panel.push({ kind: 'organization', id: t.organization_id }),
                },
                // A linked task climbs to the first task whose run submitted it (Feature 71).
                ...(t.parent_task_id
                  ? [
                      {
                        icon: 'account_tree',
                        label: shortId(t.parent_task_id),
                        meta: `first task · step ${t.linkage_step ?? ''}`,
                        onClick: () => panel.push({ kind: 'task', id: t.parent_task_id ?? '' }),
                      },
                    ]
                  : []),
                // A schedule run's step has no first task to climb to (Feature 77 D17).
                ...(!t.parent_task_id && t.linkage_step
                  ? [
                      {
                        icon: 'schedule',
                        label: `step ${t.linkage_step}`,
                        meta: 'a schedule run',
                      },
                    ]
                  : []),
                // The steps a call forked, each its own task (Feature 78 D17).
                ...(t.forked ?? []).map((f) => ({
                  icon: 'call_split',
                  label: shortId(f.task_id),
                  meta: `forked step ${f.step}`,
                  onClick: () => panel.push({ kind: 'task', id: f.task_id }),
                })),
                // The template its run follows, opened in its own record (Feature 77 D20).
                ...(t.linkage_template
                  ? [
                      {
                        icon: 'hub',
                        label: t.linkage_template,
                        meta: 'linkage template',
                        onClick: () =>
                          panel.push({ kind: 'linkage', id: t.linkage_template ?? '' }),
                      },
                    ]
                  : []),
              ]}
            />
          </RecordSection>
          <RecordActions>
            <RecordOpen record={record} label="Open logs" />
          </RecordActions>
        </RecordColumn>
      )}
    </RecordAsync>
  );
}
